
"use client"

import { Moon, Sun } from "lucide-react"
import { useTheme } from "next-themes"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { useEffect, useState } from "react"

interface ThemeToggleProps {
  variant?: "button" | "switch"
}

export function ThemeToggle({ variant = "switch" }: ThemeToggleProps) {
  const [mounted, setMounted] = useState(false)
  const { theme, setTheme } = useTheme()

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    // Return a placeholder during SSR
    if (variant === "button") {
      return (
        <Button variant="outline" size="icon" disabled>
          <Sun className="h-[1.2rem] w-[1.2rem]" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      )
    }
    return (
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label htmlFor="dark-mode">Dark Mode</Label>
        <p className="text-sm text-steel-silver">Toggle between light and dark themes</p>
        </div>
        <Switch id="dark-mode" disabled />
      </div>
    )
  }

  if (variant === "button") {
    return (
      <Button
        variant="outline"
        size="icon"
        onClick={() => setTheme(theme === "light" ? "dark" : "light")}
      >
        <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        <span className="sr-only">Toggle theme</span>
      </Button>
    )
  }

  return (
    <div className="flex items-center justify-between">
      <div className="space-y-1">
        <Label htmlFor="dark-mode">Dark Mode</Label>
        <p className="text-sm text-steel-silver">Toggle between light and dark themes</p>
      </div>
      <Switch
        id="dark-mode"
        checked={theme === "dark"}
        onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
      />
    </div>
  )
}
